import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AffiliateAgreementHistory,
  AffiliateAgreementWriteRequest,
  AffiliateCodeCreateRequest,
  AffiliateCodeLabelRequest,
  AffiliateDetail,
  AffiliateReferralList,
} from '@/api/generated';
import { ApiError } from '@/api/errors';
import { useTenantApi } from '@/tenant/useTenant';

export interface AffiliateData {
  detail: AffiliateDetail;
  /** `null` while the agreement is still loading, or when that request failed. */
  agreement: AffiliateAgreementHistory | null;
  /** `null` while referrals are still loading, or when that request failed. */
  referrals: AffiliateReferralList | null;
}

/** A side load. A failure here does not blank the affiliate detail. */
export interface AffiliatePart {
  status: 'idle' | 'loading' | 'ready' | 'error';
  error: unknown;
  reload: () => void;
}

export interface AffiliateState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  data: AffiliateData | null;
  error: ApiError | null;
  /** The detail. Agreement and referrals have their own `reload`. */
  reload: () => void;
  agreement: AffiliatePart;
  referrals: AffiliatePart;
  /**
   * Refetches every part without the loading state (TMMRSL-0053): the open detail stays mounted.
   * On failure that part's current data is kept and this rejects. A late answer — this refetch,
   * `reload`, or a mutation's refetch — is applied only when it is still the latest request for
   * that part of the affiliate now open (TMMRSL-0054).
   */
  refresh: () => Promise<void>;
  approve: () => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  end: () => Promise<void>;
  saveDeal: (body: AffiliateAgreementWriteRequest) => Promise<void>;
  createCode: (body: AffiliateCodeCreateRequest) => Promise<void>;
  editCodeLabel: (codeId: number, body: AffiliateCodeLabelRequest) => Promise<void>;
  setCodeStatus: (codeId: number, status: 'PAUSED' | 'ACTIVE') => Promise<void>;
  voidReferral: (attributionId: number, reason: string) => Promise<void>;
  rename: (displayName: string) => Promise<void>;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

/** The next request number for one affiliate. A later request for the same id wins. */
function take(seq: Map<number, number>, forId: number): number {
  const token = (seq.get(forId) ?? 0) + 1;
  seq.set(forId, token);
  return token;
}

/** True when this answer is still the latest request for the affiliate now open. */
function isLatest(
  seq: Map<number, number>,
  openId: number | undefined,
  forId: number,
  token: number,
): boolean {
  return openId === forId && seq.get(forId) === token;
}

/**
 * Loads one affiliate. The detail is the page; the agreement and referrals load beside it, so a
 * failure of either leaves the detail up (TMMRSL-0055). Idle when no id.
 */
export function useAffiliate(id: number | undefined): AffiliateState {
  const [status, setStatus] = useState<AffiliateState['status']>(
    id === undefined ? 'idle' : 'loading',
  );
  const [detail, setDetail] = useState<AffiliateDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [agreement, setAgreement] = useState<AffiliateAgreementHistory | null>(null);
  const [agreementStatus, setAgreementStatus] = useState<AffiliatePart['status']>(
    id === undefined ? 'idle' : 'loading',
  );
  const [agreementError, setAgreementError] = useState<unknown>(null);
  const [referrals, setReferrals] = useState<AffiliateReferralList | null>(null);
  const [referralsStatus, setReferralsStatus] = useState<AffiliatePart['status']>(
    id === undefined ? 'idle' : 'loading',
  );
  const [referralsError, setReferralsError] = useState<unknown>(null);
  const api = useTenantApi();
  // Per part and per affiliate, so a late answer for A cannot replace B (TMMRSL-0054).
  const detailSeq = useRef(new Map<number, number>());
  const agreementSeq = useRef(new Map<number, number>());
  const referralsSeq = useRef(new Map<number, number>());
  const idRef = useRef(id);

  const loadDetail = useCallback(
    (forId: number, quiet: boolean) => {
      const token = take(detailSeq.current, forId);
      if (!quiet) {
        setStatus('loading');
        setError(null);
        setDetail(null);
      }
      return api.getAffiliate(forId).then(
        (next) => {
          if (!isLatest(detailSeq.current, idRef.current, forId, token)) return;
          setDetail(next);
          setError(null);
          setStatus('ready');
        },
        (err: unknown) => {
          if (!isLatest(detailSeq.current, idRef.current, forId, token)) return;
          if (quiet) throw err;
          setDetail(null);
          setError(toApiError(err));
          setStatus('error');
        },
      );
    },
    [api],
  );

  const loadAgreement = useCallback(
    (forId: number, quiet: boolean) => {
      const token = take(agreementSeq.current, forId);
      if (!quiet) {
        setAgreementStatus('loading');
        setAgreementError(null);
        setAgreement(null);
      }
      return api.getAffiliateAgreement(forId).then(
        (next) => {
          if (!isLatest(agreementSeq.current, idRef.current, forId, token)) return;
          setAgreement(next);
          setAgreementError(null);
          setAgreementStatus('ready');
        },
        (err: unknown) => {
          if (!isLatest(agreementSeq.current, idRef.current, forId, token)) return;
          if (quiet) throw err;
          setAgreement(null);
          setAgreementError(err);
          setAgreementStatus('error');
        },
      );
    },
    [api],
  );

  const loadReferrals = useCallback(
    (forId: number, quiet: boolean) => {
      const token = take(referralsSeq.current, forId);
      if (!quiet) {
        setReferralsStatus('loading');
        setReferralsError(null);
        setReferrals(null);
      }
      return api.listAffiliateReferrals(forId).then(
        (next) => {
          if (!isLatest(referralsSeq.current, idRef.current, forId, token)) return;
          setReferrals(next);
          setReferralsError(null);
          setReferralsStatus('ready');
        },
        (err: unknown) => {
          if (!isLatest(referralsSeq.current, idRef.current, forId, token)) return;
          if (quiet) throw err;
          setReferrals(null);
          setReferralsError(err);
          setReferralsStatus('error');
        },
      );
    },
    [api],
  );

  const reloadAgreement = useCallback(() => {
    if (id === undefined) {
      setAgreement(null);
      setAgreementError(null);
      setAgreementStatus('idle');
      return;
    }
    void loadAgreement(id, false);
  }, [id, loadAgreement]);

  const reloadReferrals = useCallback(() => {
    if (id === undefined) {
      setReferrals(null);
      setReferralsError(null);
      setReferralsStatus('idle');
      return;
    }
    void loadReferrals(id, false);
  }, [id, loadReferrals]);

  const reload = useCallback(() => {
    if (id === undefined) {
      setDetail(null);
      setError(null);
      setStatus('idle');
      setAgreement(null);
      setAgreementError(null);
      setAgreementStatus('idle');
      setReferrals(null);
      setReferralsError(null);
      setReferralsStatus('idle');
      return;
    }
    void loadDetail(id, false);
    void loadAgreement(id, false);
    void loadReferrals(id, false);
  }, [id, loadDetail, loadAgreement, loadReferrals]);

  const refresh = useCallback(async () => {
    if (id === undefined) return;
    await Promise.all([loadDetail(id, true), loadAgreement(id, true), loadReferrals(id, true)]);
  }, [id, loadDetail, loadAgreement, loadReferrals]);

  const mutate = useCallback(
    async (fn: (currentId: number) => Promise<unknown>) => {
      if (id === undefined) return;
      await fn(id);
      await refresh();
    },
    [id, refresh],
  );

  const approve = useCallback(
    () => mutate((current) => api.approveAffiliate(current)),
    [api, mutate],
  );
  const pause = useCallback(() => mutate((current) => api.pauseAffiliate(current)), [api, mutate]);
  const resume = useCallback(
    () => mutate((current) => api.resumeAffiliate(current)),
    [api, mutate],
  );
  const end = useCallback(() => mutate((current) => api.endAffiliate(current)), [api, mutate]);
  const saveDeal = useCallback(
    (body: AffiliateAgreementWriteRequest) =>
      mutate((current) => api.putAffiliateAgreement(current, body)),
    [api, mutate],
  );
  const createCode = useCallback(
    (body: AffiliateCodeCreateRequest) =>
      mutate((current) => api.createAffiliateCode(current, body)),
    [api, mutate],
  );
  const editCodeLabel = useCallback(
    (codeId: number, body: AffiliateCodeLabelRequest) =>
      mutate((current) => api.patchAffiliateCode(current, codeId, body)),
    [api, mutate],
  );
  const setCodeStatus = useCallback(
    (codeId: number, nextStatus: 'PAUSED' | 'ACTIVE') =>
      mutate((current) =>
        nextStatus === 'PAUSED'
          ? api.pauseAffiliateCode(current, codeId)
          : api.resumeAffiliateCode(current, codeId),
      ),
    [api, mutate],
  );
  const voidReferral = useCallback(
    (attributionId: number, reason: string) =>
      mutate(() => api.voidReferral(attributionId, { reason })),
    [api, mutate],
  );
  const rename = useCallback(
    (displayName: string) => mutate((current) => api.patchAffiliate(current, { displayName })),
    [api, mutate],
  );

  useEffect(() => {
    idRef.current = id;
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const data: AffiliateData | null = detail ? { detail, agreement, referrals } : null;

  return {
    status,
    data,
    error,
    reload,
    agreement: { status: agreementStatus, error: agreementError, reload: reloadAgreement },
    referrals: { status: referralsStatus, error: referralsError, reload: reloadReferrals },
    refresh,
    approve,
    pause,
    resume,
    end,
    saveDeal,
    createCode,
    editCodeLabel,
    setCodeStatus,
    voidReferral,
    rename,
  };
}
